<template>
  <div class="pnl-mini-card">
    <div class="pnl-mini-head">
      <div class="pnl-mini-title-block">
        <span class="pnl-mini-title">Performance</span>
        <span class="pnl-mini-range">· {{ rangeLabel }}</span>
      </div>
      <div class="pnl-mini-pills">
        <button
          v-for="option in rangeOptions"
          :key="option.value"
          class="pnl-mini-pill"
          :class="{ active: range === option.value }"
          @click="setRange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="pnl-mini-loading">
      <span class="pnl-mini-dot" />
      <span class="pnl-mini-dot" />
      <span class="pnl-mini-dot" />
    </div>
    <template v-else>
      <div
        class="pnl-mini-total"
        :class="rangeError ? 'muted' : summary.total >= 0 ? 'positive' : 'negative'"
      >
        {{ rangeError ? '&mdash;' : formatSignedMoney(summary.total) }}
      </div>

      <div v-if="rangeError" class="pnl-mini-empty">
        Couldn't load this range right now.
      </div>
      <div v-else-if="chartPoints.length < 2" class="pnl-mini-empty">
        Not enough settled bets in this range to chart yet.
      </div>
      <svg
        v-else
        class="pnl-mini-chart"
        :viewBox="`0 0 ${chartW} ${chartH}`"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="pnlMiniFillGreen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#009f25" stop-opacity="0.3" />
            <stop offset="100%" stop-color="#009f25" stop-opacity="0.02" />
          </linearGradient>
          <linearGradient id="pnlMiniFillRed" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#dc3545" stop-opacity="0.02" />
            <stop offset="100%" stop-color="#dc3545" stop-opacity="0.3" />
          </linearGradient>
        </defs>
        <line
          :x1="0"
          :x2="chartW"
          :y1="zeroY"
          :y2="zeroY"
          class="pnl-mini-zero"
          vector-effect="non-scaling-stroke"
        />
        <path
          :d="areaPath"
          :fill="lineIsPositive ? 'url(#pnlMiniFillGreen)' : 'url(#pnlMiniFillRed)'"
        />
        <path
          :d="linePath"
          fill="none"
          :stroke="lineIsPositive ? '#009f25' : '#dc3545'"
          stroke-width="2"
          vector-effect="non-scaling-stroke"
          stroke-linejoin="round"
          stroke-linecap="round"
        />
      </svg>

      <div v-if="!rangeError && chartPoints.length >= 2" class="pnl-mini-axis">
        <span>{{ chartPoints[0].dateLabel }}</span>
        <span>{{ chartPoints[chartPoints.length - 1].dateLabel }}</span>
      </div>

      <div v-if="!rangeError" class="pnl-mini-stats">
        <div class="pnl-mini-stat">
          <span class="pnl-mini-stat-label">Settled bets</span>
          <span class="pnl-mini-stat-value">{{
            summary.settledBets.toLocaleString()
          }}</span>
        </div>
        <div class="pnl-mini-stat">
          <span class="pnl-mini-stat-label">Best sport</span>
          <span
            v-if="bestLeague"
            class="pnl-mini-stat-value positive"
          >
            {{ bestLeague.league }} {{ formatSignedMoney(bestLeague.pnl) }}
          </span>
          <span v-else class="pnl-mini-stat-value">&mdash;</span>
        </div>
        <div class="pnl-mini-stat">
          <span class="pnl-mini-stat-label">Worst sport</span>
          <span
            v-if="worstLeague"
            class="pnl-mini-stat-value negative"
          >
            {{ worstLeague.league }} {{ formatSignedMoney(worstLeague.pnl) }}
          </span>
          <span v-else class="pnl-mini-stat-value">&mdash;</span>
        </div>
      </div>

      <button type="button" class="pnl-mini-link" @click="openFullView">
        View full performance
        <font-awesome-icon :icon="faArrowRight" class="pnl-mini-link-icon" />
      </button>
    </template>

    <!-- The full breakdown opens in place rather than navigating away, so
         this card works as a standalone home for the feature. -->
    <wide-modal v-if="fullViewOpen" @close-modal="closeFullView">
      <pnl-performance />
    </wide-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { useAPI } from '@/composables/useAPI';
import { buildChartPoints, formatSignedMoney } from '@/core/pnlUtils';
import WideModal from '@/components/global/WideModal.vue';
import PnlPerformance from '@/components/userBets/PnlPerformance.vue';

const { get: getPnlSummary } = useAPI('user/getPnlSummary');

// Account-page widget: a week by default (Polymarket-style).
const rangeOptions = [
  { value: '1d', label: '1D' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: 'all', label: 'All' },
];

const range = ref('7d');
const loading = ref(true);
const rangeError = ref(false);
const fullViewOpen = ref(false);

function onKeydown(event) {
  if (event.key === 'Escape') closeFullView();
}

function openFullView() {
  fullViewOpen.value = true;
  // Keep the page behind the modal from scrolling under it on mobile, where
  // the modal is full-bleed.
  document.body.style.overflow = 'hidden';
  window.addEventListener('keydown', onKeydown);
}

function closeFullView() {
  fullViewOpen.value = false;
  document.body.style.overflow = '';
  window.removeEventListener('keydown', onKeydown);
}

// Unmounting while open (route change) must not leave the page unscrollable.
onUnmounted(() => {
  document.body.style.overflow = '';
  window.removeEventListener('keydown', onKeydown);
});

const emptySummary = () => ({
  total: 0,
  settledBets: 0,
  daily: [],
  byLeague: [],
  granularity: 'day',
  effectiveRange: null,
});
const summary = ref(emptySummary());
const cache = {};
let requestCounter = 0;

const chartW = 640;
const chartH = 120;

const RANGE_LABELS = {
  '1d': 'Last 24 Hours',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  all: 'All Time',
};

const rangeLabel = computed(
  () => RANGE_LABELS[summary.value.effectiveRange || range.value] || RANGE_LABELS.all,
);

async function fetchRange(value) {
  if (cache[value]) {
    requestCounter += 1;
    summary.value = cache[value];
    loading.value = false;
    rangeError.value = false;
    return;
  }
  const requestId = ++requestCounter;
  loading.value = true;
  rangeError.value = false;
  try {
    const data = await getPnlSummary({ range: value });
    const clean = data
      ? {
          total: data.total || 0,
          settledBets: data.settledBets || 0,
          daily: data.daily || [],
          byLeague: data.byLeague || [],
          granularity: data.granularity || 'day',
          // The server echoes the range it actually used. An older core-api
          // that doesn't know a range serves all-time instead, and the label
          // must follow the data rather than the button we pressed.
          effectiveRange: data.range || value,
        }
      : emptySummary();
    cache[value] = clean;
    if (requestId === requestCounter) summary.value = clean;
  } catch {
    if (requestId === requestCounter) {
      summary.value = emptySummary();
      rangeError.value = true;
    }
  } finally {
    if (requestId === requestCounter) loading.value = false;
  }
}

function setRange(value) {
  if (range.value === value) return;
  range.value = value;
  fetchRange(value);
}

onMounted(() => fetchRange(range.value));

const chartPoints = computed(() =>
  buildChartPoints(summary.value.daily, summary.value.granularity, {
    withYear: (summary.value.effectiveRange || range.value) === 'all',
  }),
);

const yDomain = computed(() => {
  const values = chartPoints.value.map((p) => p.cumulative);
  let min = Math.min(0, ...values);
  let max = Math.max(0, ...values);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * 0.08;
  return { min: min - pad, max: max + pad };
});

function yFor(value) {
  const { min, max } = yDomain.value;
  return chartH - ((value - min) / (max - min)) * chartH;
}

function xFor(index) {
  const n = chartPoints.value.length;
  return n <= 1 ? 0 : (index / (n - 1)) * chartW;
}

const zeroY = computed(() => yFor(0));

const linePath = computed(() =>
  chartPoints.value
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(2)},${yFor(p.cumulative).toFixed(2)}`,
    )
    .join(''),
);

const areaPath = computed(() => {
  if (!chartPoints.value.length) return '';
  const z = zeroY.value.toFixed(2);
  return `M0,${z}${chartPoints.value
    .map((p, i) => `L${xFor(i).toFixed(2)},${yFor(p.cumulative).toFixed(2)}`)
    .join('')}L${chartW},${z}Z`;
});

const lineIsPositive = computed(() => {
  const points = chartPoints.value;
  return !points.length || points[points.length - 1].cumulative >= 0;
});

// byLeague arrives sorted by pnl desc from the endpoint.
const bestLeague = computed(() => {
  const rows = summary.value.byLeague;
  return rows.length && rows[0].pnl > 0 ? rows[0] : null;
});

const worstLeague = computed(() => {
  const rows = summary.value.byLeague;
  const last = rows[rows.length - 1];
  return rows.length && last.pnl < 0 ? last : null;
});

</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

/* Matches the account page's portfolio-card look. */
.pnl-mini-card {
  display: flex;
  flex-direction: column;
  background:
    linear-gradient(135deg, rgba(99, 102, 241, 0.05), rgba(139, 92, 246, 0.03)),
    var(--white_01);
  border: 1px solid var(--grey-1901);
  border-radius: 14px;
  padding: 1rem 1.25rem;
}

.pnl-mini-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.pnl-mini-title-block {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
}

.pnl-mini-title {
  color: main.$color-primary_01;
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-semibold;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.pnl-mini-range {
  color: main.$text-gray;
  font-size: main.$font-size-sm;
}

.pnl-mini-pills {
  display: flex;
  gap: 0.3rem;
}

.pnl-mini-pill {
  border: 1px solid main.$blue-1400;
  background: main.$white_03;
  color: main.$color-primary_01;
  border-radius: 999px;
  font-size: main.$font-size-xs;
  padding: 0.2rem 0.65rem;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:focus {
    outline: none;
  }

  &.active {
    box-shadow:
      0 0 0 1px rgba(120, 152, 255, 0.45),
      0 0 10px rgba(120, 152, 255, 0.5);
    font-weight: main.$font-weight-semibold;
  }
}

.pnl-mini-total {
  font-size: 1.75rem;
  font-weight: bold;
  line-height: 1.2;
  margin: 0.25rem 0 0.5rem;

  &.muted {
    color: main.$text-gray;
  }
}

/* Height-matched to the card's loaded state (total + chart + axis +
   stats) so the page below doesn't jump when the fetch resolves. The
   global Spinner is wrong here: it forces min-height 535px under sm. */
.pnl-mini-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 268px;
}

.pnl-mini-dot {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background-color: main.$blue-100;
  display: inline-block;
  animation: pnl-mini-bounce 1.4s infinite ease-in-out both;

  &:nth-child(1) {
    animation-delay: -0.32s;
  }

  &:nth-child(2) {
    animation-delay: -0.16s;
  }
}

@keyframes pnl-mini-bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.pnl-mini-empty {
  color: main.$text-gray;
  font-size: main.$font-size-sm;
  padding: 1.25rem 0;
  text-align: center;
}

.pnl-mini-chart {
  display: block;
  width: 100%;
  height: 120px;
}

.pnl-mini-zero {
  stroke: main.$text-gray;
  stroke-width: 1;
  stroke-dasharray: 4 4;
  opacity: 0.5;
}

.pnl-mini-axis {
  display: flex;
  justify-content: space-between;
  color: main.$text-gray;
  font-size: main.$font-size-xs;
  margin-top: 0.2rem;
}

.pnl-mini-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--grey-1901);
}

.pnl-mini-stat {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.pnl-mini-stat-label {
  color: main.$text-gray;
  font-size: main.$font-size-xs;
  white-space: nowrap;
}

.pnl-mini-stat-value {
  color: main.$color-primary_01;
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-semibold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pnl-mini-link {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.75rem;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: main.$color-primary_01;
  font-size: main.$font-size-sm;
  font-weight: main.$font-weight-semibold;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid main.$blue-100;
    outline-offset: 3px;
    border-radius: 3px;
  }
}

/* The card is a flex column; the modal is fixed-position chrome and must not
   participate in that column's spacing. */
.pnl-mini-card :deep(.modal-wrap) {
  text-align: left;
}

.pnl-mini-link-icon {
  font-size: 0.75em;
}

.positive {
  color: main.$green-700;
}

.negative {
  color: main.$red-500;
}

/* Mobile: stats wrap two-up so labels stay readable. */
@media screen and (max-width: (map.get(main.$grid-breakpoints, md) - 0.02)) {
  .pnl-mini-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pnl-mini-loading {
    height: 337px;
  }
}
</style>
